/**
 * Regras da foto do responsável no cadastro de pessoas.
 *
 * Existe porque a foto passa a ser **enviada pelo painel**, e não mais hospedada manualmente. As
 * mesmas regras precisam valer no navegador (feedback imediato ao escolher o arquivo) e na Lambda
 * (validação real antes de gerar a URL assinada) — por isso vivem no núcleo.
 *
 * Restrição arquitetural: este módulo não calcula hash. Hash exige API de plataforma
 * (`crypto.subtle` no navegador, `node:crypto` na Lambda), e o núcleo não pode depender de nenhuma
 * das duas. O hash é calculado por quem chama e entregue pronto a `montarChaveFoto`.
 */

/** Tipos aceitos. PNG é o padrão da marca; JPEG e WebP são aceitos e convertidos no envio. */
export const TIPOS_IMAGEM_ACEITOS = ['image/png', 'image/jpeg', 'image/webp'] as const;
export type TipoImagemAceito = (typeof TIPOS_IMAGEM_ACEITOS)[number];

/** Limite de 2 MB. Uma foto de 256×256 em PNG fica na ordem de dezenas de kilobytes. */
export const TAMANHO_MAXIMO_BYTES = 2 * 1024 * 1024;

/** Dimensão mínima aceita. Abaixo disso a foto fica visivelmente borrada no card de contato. */
export const DIMENSAO_MINIMA_PX = 128;

/** Dimensão canônica de saída, conforme `design-system.md`: renderiza a 64 px, 4× para retina. */
export const DIMENSAO_CANONICA_PX = 256;

/** Tolerância de proporção. A foto é exibida com `rounded-full` e `object-cover`. */
const RAZAO_MAXIMA = 1.2;

export interface FotoCandidata {
  readonly tipo: string;
  readonly tamanhoBytes: number;
  readonly largura: number;
  readonly altura: number;
}

export type CodigoProblemaFoto =
  'TIPO_NAO_ACEITO' | 'ARQUIVO_VAZIO' | 'ARQUIVO_GRANDE' | 'DIMENSAO_PEQUENA' | 'MUITO_RETANGULAR';

export interface ProblemaFoto {
  readonly codigo: CodigoProblemaFoto;
  readonly mensagem: string;
}

/**
 * Valida a foto escolhida antes do envio.
 *
 * Devolve a lista de problemas em vez de lançar, para o formulário poder exibir todos de uma vez.
 */
export function validarFoto(foto: FotoCandidata): readonly ProblemaFoto[] {
  const problemas: ProblemaFoto[] = [];

  if (!(TIPOS_IMAGEM_ACEITOS as readonly string[]).includes(foto.tipo)) {
    problemas.push({
      codigo: 'TIPO_NAO_ACEITO',
      mensagem: `Formato não aceito: ${foto.tipo || 'desconhecido'}. Use PNG, JPEG ou WebP.`,
    });
  }

  if (foto.tamanhoBytes <= 0) {
    problemas.push({ codigo: 'ARQUIVO_VAZIO', mensagem: 'O arquivo está vazio.' });
  } else if (foto.tamanhoBytes > TAMANHO_MAXIMO_BYTES) {
    const mb = (foto.tamanhoBytes / 1024 / 1024).toFixed(1);
    problemas.push({
      codigo: 'ARQUIVO_GRANDE',
      mensagem: `A imagem tem ${mb} MB. O limite é 2 MB.`,
    });
  }

  const menor = Math.min(foto.largura, foto.altura);
  const maior = Math.max(foto.largura, foto.altura);

  if (menor < DIMENSAO_MINIMA_PX) {
    problemas.push({
      codigo: 'DIMENSAO_PEQUENA',
      mensagem: `A imagem tem ${foto.largura}×${foto.altura} px. O mínimo é ${DIMENSAO_MINIMA_PX}×${DIMENSAO_MINIMA_PX} px.`,
    });
  } else if (maior / menor > RAZAO_MAXIMA) {
    problemas.push({
      codigo: 'MUITO_RETANGULAR',
      mensagem:
        'A imagem é muito retangular e será cortada em círculo. Use um recorte aproximadamente quadrado, com o rosto centralizado.',
    });
  }

  return problemas;
}

/**
 * Monta a chave de armazenamento da foto.
 *
 * A chave inclui o hash do conteúdo de propósito. Trocar a foto gera **uma chave nova**, o que
 * significa:
 *
 * - nenhuma invalidação de cache no CloudFront é necessária, preservando a cota gratuita
 *   (decisão D10.1);
 * - propostas já publicadas continuam apontando para a foto vigente na data da emissão, o que é o
 *   comportamento correto para um documento comercial imutável.
 *
 * @param sub Identificador do usuário no Cognito.
 * @param hashConteudo Hash hexadecimal do conteúdo, calculado pelo chamador.
 */
export function montarChaveFoto(sub: string, hashConteudo: string): string {
  const subLimpo = sub.trim().toLowerCase();
  const hashCurto = hashConteudo.trim().toLowerCase().slice(0, 12);

  if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(subLimpo)) {
    throw new Error('A chave da foto exige o sub do Cognito no formato 8-4-4-4-12.');
  }
  if (!/^[0-9a-f]{12}$/.test(hashCurto)) {
    throw new Error('O hash do conteúdo deve ser hexadecimal com ao menos 12 caracteres.');
  }

  return `pessoas/${subLimpo}/${hashCurto}.png`;
}
